export default function TermsPage() {
  return (
    <>
      <section className="py-20 sm:py-28 bg-gradient-to-br from-primary-700 via-primary-600 to-primary-700">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="text-4xl font-bold tracking-tight text-white sm:text-5xl lg:text-6xl">
              Terms of Service
            </h1>
            <p className="mt-6 text-lg leading-8 text-primary-100 sm:text-xl">
              Last updated: January 16, 2025
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="space-y-8 text-navy-600">
            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">1. Agreement to Terms</h2>
              <p className="mb-4 leading-relaxed">
                These Terms of Service ("Terms") constitute a legally binding agreement between you
                ("Customer," "you," or "your") and Clarantis, Inc. ("Clarantis," "we," "us," or "our")
                governing your access to and use of the Clarantis governance, risk, and compliance
                platform, including any related services, features, content, and applications
                (collectively, the "Services").
              </p>
              <p className="leading-relaxed">
                By accessing or using our Services, you agree to be bound by these Terms. If you do
                not agree to these Terms, you may not access or use the Services. If you are using the
                Services on behalf of an organization, you represent and warrant that you have authority
                to bind that organization to these Terms.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">2. Description of Services</h2>
              <p className="mb-3 leading-relaxed">
                Clarantis provides a cloud-based platform for governance, risk, and compliance management,
                including but not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Compliance framework management and tracking</li>
                <li>Policy and document management</li>
                <li>Risk assessment and monitoring</li>
                <li>Evidence collection and management</li>
                <li>Vendor and third-party risk management</li>
                <li>Task management and workflow automation</li>
                <li>Reporting and analytics</li>
                <li>Integration with third-party services</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">3. Account Registration and Security</h2>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">3.1 Account Creation</h3>
              <p className="mb-4 leading-relaxed">
                To use the Services, you must create an account by providing accurate, complete, and
                current information. You agree to update your information to keep it accurate and current.
              </p>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">3.2 Account Security</h3>
              <p className="mb-3 leading-relaxed">
                You are responsible for maintaining the confidentiality of your account credentials and
                for all activities that occur under your account. You agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li>Use strong, unique passwords</li>
                <li>Enable multi-factor authentication when available</li>
                <li>Notify us immediately of any unauthorized access</li>
                <li>Not share your credentials with others</li>
              </ul>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">3.3 User Management</h3>
              <p className="leading-relaxed">
                Account administrators are responsible for managing user access, permissions, and ensuring
                users comply with these Terms.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">4. Acceptable Use</h2>
              <p className="mb-3 leading-relaxed">You agree to use the Services only for lawful purposes and in accordance with these Terms. You agree not to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Violate any applicable laws, regulations, or third-party rights</li>
                <li>Upload malicious code, viruses, or harmful content</li>
                <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
                <li>Interfere with or disrupt the Services or servers</li>
                <li>Use the Services to store or transmit infringing or illegal content</li>
                <li>Reverse engineer, decompile, or disassemble the Services</li>
                <li>Resell, sublicense, or redistribute the Services without authorization</li>
                <li>Use automated means to access the Services without permission</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">5. Customer Data</h2>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">5.1 Ownership</h3>
              <p className="mb-4 leading-relaxed">
                You retain all rights, title, and interest in and to your data uploaded to or created
                within the Services ("Customer Data"). Clarantis does not claim ownership of Customer Data.
              </p>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">5.2 License to Clarantis</h3>
              <p className="mb-4 leading-relaxed">
                You grant Clarantis a limited, non-exclusive license to use, process, and store Customer
                Data solely to provide and improve the Services. This license terminates when you delete
                your Customer Data or close your account.
              </p>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">5.3 Data Responsibility</h3>
              <p className="leading-relaxed">
                You are solely responsible for the accuracy, quality, and legality of Customer Data and
                the means by which you acquired it. You represent that you have all necessary rights to
                upload and use Customer Data within the Services.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">6. Intellectual Property</h2>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">6.1 Clarantis IP</h3>
              <p className="mb-4 leading-relaxed">
                The Services, including all software, features, designs, text, graphics, and other content,
                are owned by Clarantis or its licensors and are protected by intellectual property laws.
                Except for the limited rights granted herein, Clarantis retains all rights in the Services.
              </p>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">6.2 Feedback</h3>
              <p className="leading-relaxed">
                If you provide feedback, suggestions, or ideas about the Services ("Feedback"), you grant
                Clarantis a perpetual, irrevocable, worldwide license to use such Feedback without
                restriction or compensation.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">7. Third-Party Integrations</h2>
              <p className="leading-relaxed">
                The Services may integrate with third-party applications and services. Your use of
                third-party integrations is subject to the terms and privacy policies of those third
                parties. Clarantis is not responsible for third-party services and does not endorse
                or warrant their functionality or security.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">8. Fees and Payment</h2>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">8.1 Subscription Fees</h3>
              <p className="mb-4 leading-relaxed">
                Access to the Services requires payment of subscription fees as specified in your order
                or agreement. Fees are non-refundable except as expressly stated herein or required by law.
              </p>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">8.2 Payment Terms</h3>
              <p className="mb-4 leading-relaxed">
                Fees are due as specified in your invoice. Late payments may incur interest at 1.5% per
                month or the maximum rate permitted by law. We may suspend access for non-payment.
              </p>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">8.3 Price Changes</h3>
              <p className="leading-relaxed">
                We may change fees upon 30 days' notice before your next renewal period. Continued use
                after a price change constitutes acceptance of the new fees.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">9. Term and Termination</h2>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">9.1 Term</h3>
              <p className="mb-4 leading-relaxed">
                These Terms are effective until terminated. Subscription terms are specified in your
                order and automatically renew unless cancelled before the renewal date.
              </p>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">9.2 Termination for Convenience</h3>
              <p className="mb-4 leading-relaxed">
                Either party may terminate with 30 days' written notice before the end of the current
                subscription term.
              </p>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">9.3 Termination for Cause</h3>
              <p className="mb-4 leading-relaxed">
                Either party may terminate immediately if the other party materially breaches these Terms
                and fails to cure within 30 days of notice.
              </p>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">9.4 Effect of Termination</h3>
              <p className="leading-relaxed">
                Upon termination, your right to use the Services ceases. You may export your Customer
                Data for 30 days after termination. After this period, we may delete your data.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">10. Warranties and Disclaimers</h2>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">10.1 Service Warranty</h3>
              <p className="mb-4 leading-relaxed">
                Clarantis warrants that the Services will perform substantially in accordance with our
                documentation during your subscription term.
              </p>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">10.2 Disclaimer</h3>
              <p className="mb-4 leading-relaxed uppercase text-sm font-medium">
                EXCEPT AS EXPRESSLY PROVIDED HEREIN, THE SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTY
                OF ANY KIND. CLARANTIS DISCLAIMS ALL IMPLIED WARRANTIES, INCLUDING MERCHANTABILITY,
                FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE
                SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, OR COMPLETELY SECURE.
              </p>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">10.3 Compliance Disclaimer</h3>
              <p className="leading-relaxed">
                The Services are tools to assist with compliance management. Clarantis does not guarantee
                that use of the Services will result in compliance with any particular law, regulation,
                or standard. You are solely responsible for determining your compliance obligations and
                ensuring you meet them.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">11. Limitation of Liability</h2>
              <p className="mb-4 leading-relaxed uppercase text-sm font-medium">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, CLARANTIS SHALL NOT BE LIABLE FOR ANY INDIRECT,
                INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, REVENUE,
                DATA, OR USE, ARISING FROM OR RELATED TO THESE TERMS OR THE SERVICES.
              </p>
              <p className="leading-relaxed uppercase text-sm font-medium">
                CLARANTIS'S TOTAL LIABILITY FOR ALL CLAIMS ARISING FROM OR RELATED TO THESE TERMS OR
                THE SERVICES SHALL NOT EXCEED THE AMOUNTS PAID BY YOU IN THE TWELVE (12) MONTHS
                PRECEDING THE CLAIM.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">12. Indemnification</h2>
              <p className="leading-relaxed">
                You agree to indemnify, defend, and hold harmless Clarantis and its officers, directors,
                employees, and agents from any claims, damages, losses, and expenses (including reasonable
                attorneys' fees) arising from your use of the Services, your Customer Data, or your
                violation of these Terms.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">13. Confidentiality</h2>
              <p className="leading-relaxed">
                Each party agrees to protect the other's confidential information using the same degree
                of care it uses to protect its own confidential information, but no less than reasonable
                care. Confidential information may only be used to exercise rights and fulfill obligations
                under these Terms.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">14. Modifications to Terms</h2>
              <p className="leading-relaxed">
                We may modify these Terms at any time. We will provide notice of material changes at
                least 30 days before they take effect. Your continued use of the Services after
                modifications constitutes acceptance of the updated Terms.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">15. General Provisions</h2>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">15.1 Governing Law</h3>
              <p className="mb-4 leading-relaxed">
                These Terms are governed by the laws of the State of Delaware, without regard to conflict
                of laws principles.
              </p>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">15.2 Dispute Resolution</h3>
              <p className="mb-4 leading-relaxed">
                Any disputes arising from these Terms shall be resolved through binding arbitration in
                accordance with the rules of the American Arbitration Association, except that either
                party may seek injunctive relief in court.
              </p>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">15.3 Entire Agreement</h3>
              <p className="mb-4 leading-relaxed">
                These Terms, together with any applicable order forms and policies referenced herein,
                constitute the entire agreement between you and Clarantis regarding the Services.
              </p>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">15.4 Severability</h3>
              <p className="mb-4 leading-relaxed">
                If any provision of these Terms is found unenforceable, the remaining provisions will
                continue in effect.
              </p>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">15.5 Waiver</h3>
              <p className="mb-4 leading-relaxed">
                Failure to enforce any provision of these Terms does not constitute a waiver of that
                provision or any other provision.
              </p>

              <h3 className="text-xl font-semibold text-primary-600 mb-3">15.6 Assignment</h3>
              <p className="leading-relaxed">
                You may not assign these Terms without our prior written consent. Clarantis may assign
                these Terms in connection with a merger, acquisition, or sale of assets.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-primary-700 mb-4">16. Contact Information</h2>
              <p className="mb-3 leading-relaxed">
                For questions about these Terms, please contact us:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Email: <a href="mailto:legal@clarantis.com" className="text-primary-600 hover:text-primary-700 underline">legal@clarantis.com</a></li>
                <li>Address: Clarantis, Inc., Legal Department</li>
              </ul>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
